import { useState, type ReactNode } from 'react';
import './insights.css';

export interface SortColumn {
  heading: string;
  /** Numbers sort largest first on the first press and sit right-aligned. */
  numeric?: boolean;
}

export interface SortRow {
  key: string;
  /** What each cell shows, already formatted. */
  cells: readonly ReactNode[];
  /** What each cell sorts by: the raw number, or text. */
  values: readonly (string | number)[];
}

export interface SortableTableProps {
  columns: readonly SortColumn[];
  rows: readonly SortRow[];
  /** Spoken name of the table. */
  caption: string;
  /** The column it opens sorted by, and which way; as given when absent. */
  initial?: { by: number; descending: boolean };
}

type Sort = { by: number; descending: boolean } | null;

function sorted(rows: readonly SortRow[], sort: Sort): readonly SortRow[] {
  if (!sort) return rows;
  const sign = sort.descending ? -1 : 1;
  return [...rows].sort((a, b) => {
    const x = a.values[sort.by] ?? '';
    const y = b.values[sort.by] ?? '';
    const order =
      typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
    return order * sign;
  });
}

function Heading(props: {
  column: SortColumn;
  index: number;
  sort: Sort;
  onSort: (index: number) => void;
}): ReactNode {
  const { column, index, sort, onSort } = props;
  const on = sort?.by === index;
  const direction = on ? (sort.descending ? 'descending' : 'ascending') : 'none';
  return (
    <th scope="col" aria-sort={direction} className={column.numeric ? 'is-numeric' : undefined}>
      <button type="button" onClick={() => onSort(index)}>
        {column.heading}
        <span aria-hidden="true" className="ad-sort__arrow">
          {on ? (sort.descending ? '↓' : '↑') : '↕'}
        </span>
      </button>
    </th>
  );
}

/**
 * A table whose headings sort it: a number column largest first on the first press, text A to Z,
 * a second press the other way. The first column names the row.
 */
export function SortableTable({ columns, rows, caption, initial }: SortableTableProps): ReactNode {
  const [sort, setSort] = useState<Sort>(initial ?? null);
  const onSort = (by: number) =>
    setSort((s) =>
      s?.by === by
        ? { by, descending: !s.descending }
        : { by, descending: Boolean(columns[by]?.numeric) },
    );
  return (
    <table className="ad-sort">
      <caption className="pui-visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {columns.map((c, i) => (
            <Heading key={c.heading} column={c} index={i} sort={sort} onSort={onSort} />
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted(rows, sort).map((row) => (
          <tr key={row.key}>
            {row.cells.map((cell, i) =>
              i === 0 ? (
                <th key={i} scope="row">
                  {cell}
                </th>
              ) : (
                <td key={i} className={columns[i]?.numeric ? 'is-numeric' : undefined}>
                  {cell}
                </td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
